import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import SalesProformaApi from "../../../../../infrastructure/api/salesProforma";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliFullDate } from "../../../../middleware/middleware";

const SOURCE = "SolarentOperator";
const TAX_RATE = 0.1;
const STATUS_DRAFT = 0;
const STATUS_ISSUED = 1;
const STATUS_FINALIZED = 3;
const DEFAULT_NOTES =
  "• زمان و محل تحویل کالا با توافق طرفین تعیین می‌شود. هزینه بارگیری، بسته‌بندی و ارسال بر عهده خریدار است\n• هرگونه خسارت پس از تحویل سفارش بر عهده خریدار خواهد بود";

const pick = (raw, camel) =>
  raw?.[camel] ?? raw?.[camel.charAt(0).toUpperCase() + camel.slice(1)] ?? null;

const emptyLine = () => ({
  title: "",
  unit: "عدد",
  quantity: 1,
  unitPriceRial: 0,
  discountRial: 0,
});

const emptyForm = () => ({
  id: null,
  status: STATUS_DRAFT,
  proformaNumber: null,
  salesCustomerId: null,
  requestTrackingCode: "",
  buyerName: "",
  buyerMobile: "",
  buyerNationalId: "",
  buyerPostalCode: "",
  buyerEconomicCode: "",
  buyerAddress: "",
  notes: DEFAULT_NOTES,
  validityHours: 24,
  lines: [emptyLine()],
});

const formatRial = (value) =>
  Number(value) > 0 ? `${Number(value).toLocaleString("fa-IR")} ریال` : "۰ ریال";

const computeLine = (line) => {
  const qty = Number(line.quantity) > 0 ? Number(line.quantity) : 1;
  const unitPrice = Math.round(Number(line.unitPriceRial) || 0);
  const discount = Math.max(0, Math.round(Number(line.discountRial) || 0));
  const after = Math.max(0, Math.round(unitPrice * qty) - discount);
  const tax = Math.round(after * TAX_RATE);
  return {
    ...line,
    quantity: qty,
    unitPriceRial: unitPrice,
    discountRial: discount,
    after,
    tax,
    total: after + tax,
  };
};

const unwrapBlob = async (response) => {
  const blob = response?.data ?? response;
  if (blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    let msg = "خطا در دانلود فایل";
    try {
      const json = JSON.parse(text);
      msg = json.message || json.Message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return blob;
};

const downloadFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
};

const mapDtoToForm = (data) => {
  const lines = (pick(data, "lines") || []).map((line) => ({
    title: pick(line, "title") || "",
    unit: pick(line, "unit") || "عدد",
    quantity: Number(pick(line, "quantity")) || 1,
    unitPriceRial: Number(pick(line, "unitPriceRial")) || 0,
    discountRial: Number(pick(line, "discountRial")) || 0,
  }));
  return {
    id: pick(data, "id") || null,
    status: Number(pick(data, "status") ?? STATUS_DRAFT),
    proformaNumber: pick(data, "proformaNumber") || null,
    salesCustomerId: pick(data, "salesCustomerId") || null,
    requestTrackingCode: String(pick(data, "requestTrackingCode") || ""),
    buyerName: pick(data, "buyerName") || "",
    buyerMobile: pick(data, "buyerMobile") || "",
    buyerNationalId: pick(data, "buyerNationalId") || "",
    buyerPostalCode: pick(data, "buyerPostalCode") || "",
    buyerEconomicCode: pick(data, "buyerEconomicCode") || "",
    buyerAddress: pick(data, "buyerAddress") || "",
    notes: pick(data, "notes") || DEFAULT_NOTES,
    validityHours: Number(pick(data, "validityHours")) || 24,
    lines: lines.length ? lines : [emptyLine()],
  };
};

const buildPayload = (form, computedLines) => {
  const buyerName = form.buyerName.trim();
  const lines = computedLines
    .filter((l) => l.title.trim())
    .map((l) => ({
      title: l.title,
      unit: l.unit,
      quantity: l.quantity,
      unitPriceRial: l.unitPriceRial,
      discountRial: l.discountRial,
    }));
  return {
    id: form.id || null,
    salesCustomerId: form.salesCustomerId,
    requestTrackingCode: form.requestTrackingCode ? Number(form.requestTrackingCode) : null,
    buyerName: buyerName || null,
    buyerMobile: form.buyerMobile,
    buyerNationalId: form.buyerNationalId,
    buyerPostalCode: form.buyerPostalCode,
    buyerEconomicCode: form.buyerEconomicCode,
    buyerAddress: form.buyerAddress,
    notes: form.notes,
    validityHours: Number(form.validityHours) || 24,
    lines,
  };
};

const SalesProformaPage = ({ source = SOURCE }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceNumber, setCopySourceNumber] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [customerMode, setCustomerMode] = useState("existing");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await SalesProformaApi.search(source, {
        searchText: appliedSearch.trim() || null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت پیش‌فاکتورها");
        setRows([]);
        return;
      }
      const list = pick(res?.data ?? res, "rows") || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت پیش‌فاکتورها");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, source]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadCustomers = useCallback(
    async (term) => {
      setCustomerLoading(true);
      try {
        const res = await SalesProformaApi.searchCustomers(source, term);
        const list = res?.isSuccess === false ? [] : res?.data ?? res ?? [];
        setCustomerOptions(Array.isArray(list) ? list : []);
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerLoading(false);
      }
    },
    [source]
  );

  useEffect(() => {
    if (!dialogOpen || customerMode !== "existing") {
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      await loadCustomers(customerQuery.trim());
    }, customerQuery.trim() ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerQuery, customerMode, dialogOpen, loadCustomers]);

  const computedLines = useMemo(() => form.lines.map(computeLine), [form.lines]);
  const totals = useMemo(
    () =>
      computedLines.reduce(
        (acc, line) => ({
          after: acc.after + line.after,
          tax: acc.tax + line.tax,
          total: acc.total + line.total,
        }),
        { after: 0, tax: 0, total: 0 }
      ),
    [computedLines]
  );

  const isEditingExisting = Boolean(form.id);
  const formStatus = Number(form.status ?? STATUS_DRAFT);
  const canEditCurrent = formStatus === STATUS_DRAFT || formStatus === STATUS_ISSUED;
  const canFinalizeCurrent =
    isEditingExisting && (formStatus === STATUS_DRAFT || formStatus === STATUS_ISSUED);

  const openCreateDialog = () => {
    setForm(emptyForm());
    setSelectedCustomer(null);
    setCustomerQuery("");
    setCustomerMode("existing");
    setDialogOpen(true);
  };

  const applyLoadedForm = (data) => {
    const nextForm = mapDtoToForm(data);
    setForm(nextForm);
    if (nextForm.salesCustomerId) {
      setSelectedCustomer({
        id: nextForm.salesCustomerId,
        fullName: nextForm.buyerName,
        nationalId: nextForm.buyerNationalId,
        mobile: nextForm.buyerMobile,
      });
      setCustomerMode("existing");
      setCustomerQuery(nextForm.buyerName || "");
    } else {
      setSelectedCustomer(null);
      setCustomerMode(nextForm.buyerName ? "new" : "existing");
      setCustomerQuery("");
    }
    setDialogOpen(true);
  };

  const applyCustomer = (customer) => {
    if (!customer) {
      setSelectedCustomer(null);
      setForm((f) => ({ ...f, salesCustomerId: null }));
      return;
    }
    setSelectedCustomer(customer);
    setForm((f) => ({
      ...f,
      salesCustomerId: pick(customer, "id"),
      buyerName: pick(customer, "fullName") || f.buyerName,
      buyerMobile: pick(customer, "mobile") || f.buyerMobile,
      buyerNationalId: pick(customer, "nationalId") || f.buyerNationalId,
      buyerPostalCode: pick(customer, "postalCode") || f.buyerPostalCode,
      buyerEconomicCode: pick(customer, "economicCode") || f.buyerEconomicCode,
      buyerAddress: pick(customer, "address") || f.buyerAddress,
    }));
  };

  const setLine = (index, field, value) => {
    setForm((prev) => {
      const lines = prev.lines.map((line, i) => (i === index ? { ...line, [field]: value } : line));
      return { ...prev, lines };
    });
  };

  const loadFromRequest = async () => {
    const tracking = Number(form.requestTrackingCode);
    if (!tracking) {
      toast.error("کد پیگیری درخواست را وارد کنید");
      return;
    }
    setLoadingRequest(true);
    try {
      const res = await SalesProformaApi.previewFromRequest(source, { trackingCode: tracking });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "درخواست یافت نشد");
        return;
      }
      const data = res?.data ?? res;
      setForm((prev) => ({
        ...prev,
        ...mapDtoToForm({ ...data, id: prev.id }),
        requestTrackingCode: String(pick(data, "requestTrackingCode") || tracking),
      }));
      setSelectedCustomer(null);
      setCustomerMode("new");
      toast.success("اطلاعات متقاضی و اقلام قرارداد بارگذاری شد. قیمت واحد را وارد کنید.");
    } catch (e) {
      toast.error(e?.message || "خطا در بارگذاری درخواست");
    } finally {
      setLoadingRequest(false);
    }
  };

  const openEdit = async (id) => {
    setLoadingDraft(true);
    try {
      const res = await SalesProformaApi.get(source, id);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "بارگذاری پیش‌فاکتور ناموفق بود");
        return;
      }
      const data = res?.data ?? res;
      const status = Number(pick(data, "status"));
      if (status !== STATUS_DRAFT && status !== STATUS_ISSUED) {
        toast.error("این پیش‌فاکتور قفل است؛ برای تغییر از کپی استفاده کنید");
        return;
      }
      applyLoadedForm(data);
    } catch (e) {
      toast.error(e?.message || "بارگذاری پیش‌فاکتور ناموفق بود");
    } finally {
      setLoadingDraft(false);
    }
  };

  const copyFromNumber = async () => {
    const number = Number(copySourceNumber);
    if (!number) {
      toast.error("شماره پیش‌فاکتور مبدأ را وارد کنید");
      return;
    }
    setSaving(true);
    try {
      const res = await SalesProformaApi.copyFromNumber(source, number);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "کپی پیش‌فاکتور ناموفق بود");
        return;
      }
      const copied = res?.data ?? res;
      toast.success(
        res?.message ||
          `کپی با شماره ${pick(copied, "proformaNumber") || "جدید"} ساخته شد`
      );
      setCopyDialogOpen(false);
      setCopySourceNumber("");
      applyLoadedForm(copied);
      await loadList();
    } catch (e) {
      toast.error(e?.message || "کپی پیش‌فاکتور ناموفق بود");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await SalesProformaApi.saveDraft(source, buildPayload(form, computedLines));
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ذخیره ناموفق بود");
        return;
      }
      const saved = res?.data ?? res;
      setForm((prev) => ({
        ...prev,
        id: pick(saved, "id") || prev.id,
        status: Number(pick(saved, "status") ?? prev.status),
        proformaNumber: pick(saved, "proformaNumber") || prev.proformaNumber,
      }));
      toast.success(res?.message || "ذخیره شد");
      await loadList();
    } catch (e) {
      toast.error(e?.message || "ذخیره ناموفق بود");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (!form.id) {
      toast.error("ابتدا پیش‌فاکتور را ذخیره یا صادر کنید");
      return;
    }
    if (!window.confirm("پس از نهایی‌سازی، پیش‌فاکتور قفل می‌شود و دیگر قابل ویرایش نیست. ادامه می‌دهید؟")) {
      return;
    }
    setSaving(true);
    try {
      // persist latest edits before lock
      const saveRes = await SalesProformaApi.saveDraft(source, buildPayload(form, computedLines));
      if (saveRes?.isSuccess === false) {
        toast.error(saveRes?.message || "ذخیره قبل از قفل ناموفق بود");
        return;
      }
      const res = await SalesProformaApi.finalize(source, form.id);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "نهایی‌سازی ناموفق بود");
        return;
      }
      const locked = res?.data ?? res;
      toast.success(res?.message || "پیش‌فاکتور نهایی و قفل شد");
      setDialogOpen(false);
      setForm(emptyForm());
      await loadList();
      const id = pick(locked, "id");
      if (id) await downloadPdf(id, pick(locked, "proformaNumber"));
    } catch (e) {
      toast.error(e?.message || "نهایی‌سازی ناموفق بود");
    } finally {
      setSaving(false);
    }
  };

  const issue = async () => {
    const buyerName = form.buyerName.trim() || customerQuery.trim();
    if (!buyerName) {
      toast.error("نام خریدار الزامی است");
      return;
    }
    const lines = computedLines.filter((l) => l.title.trim());
    if (!lines.length) {
      toast.error("حداقل یک قلم کالا لازم است");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload({ ...form, buyerName }, computedLines);
      const res = await SalesProformaApi.issue(source, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "صدور پیش‌فاکتور ناموفق بود");
        return;
      }
      const issued = res?.data ?? res;
      toast.success(res?.message || "پیش‌فاکتور صادر شد");
      setForm((prev) => ({
        ...prev,
        id: pick(issued, "id") || prev.id,
        status: STATUS_ISSUED,
        proformaNumber: pick(issued, "proformaNumber") || prev.proformaNumber,
      }));
      await loadList();
      const id = pick(issued, "id");
      if (id) await downloadPdf(id, pick(issued, "proformaNumber"));
    } catch (e) {
      toast.error(e?.message || "صدور پیش‌فاکتور ناموفق بود");
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("این پیش‌فاکتور حذف شود؟")) return;
    try {
      const res = await SalesProformaApi.deleteDraft(source, id);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "حذف ناموفق بود");
        return;
      }
      toast.success(res?.message || "حذف شد");
      if (form.id === id) {
        setDialogOpen(false);
        setForm(emptyForm());
      }
      await loadList();
    } catch (e) {
      toast.error(e?.message || "حذف ناموفق بود");
    }
  };

  const downloadPdf = async (id, number) => {
    try {
      const blob = await unwrapBlob(await SalesProformaApi.downloadPdf(source, id));
      downloadFile(blob, `pish-factor-${number || id}.pdf`);
    } catch (e) {
      toast.error(e?.message || "دانلود PDF ناموفق بود");
    }
  };

  const downloadExcel = async (id, number) => {
    try {
      const blob = await unwrapBlob(await SalesProformaApi.downloadExcel(source, id));
      downloadFile(blob, `pish-factor-${number || id}.xlsx`);
    } catch (e) {
      toast.error(e?.message || "دانلود اکسل ناموفق بود");
    }
  };

  const tableRows = rows.map((raw, index) => {
    const status = Number(pick(raw, "status"));
    return {
      raw,
      row_index: index + 1,
      id: pick(raw, "id"),
      proformaNumber: pick(raw, "proformaNumber"),
      issuedAtDisplay: pick(raw, "issuedAtFa") || toJalaliFullDate(pick(raw, "issuedAt")) || "—",
      buyerName: pick(raw, "buyerName") || "—",
      buyerMobile: pick(raw, "buyerMobile") || "—",
      totalDisplay: formatRial(pick(raw, "totalAmount")),
      status,
      statusTitle: pick(raw, "statusTitle") || "—",
      isDraft: status === STATUS_DRAFT,
      isIssued: status === STATUS_ISSUED,
      isFinalized: status === STATUS_FINALIZED,
      isEditable: status === STATUS_DRAFT || status === STATUS_ISSUED,
      canDownload: status === STATUS_ISSUED || status === STATUS_FINALIZED,
    };
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="صدور پیش‌فاکتور فروش"
            subheader="پیشنویس، کپی از شماره قبلی، ویرایش صادرشده، و نهایی‌سازی برای قفل"
            action={
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button startIcon={<SyncIcon />} onClick={loadList} disabled={loading} variant="outlined" size="small">
                  بروزرسانی
                </Button>
                <Button
                  startIcon={<ContentCopyIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setCopySourceNumber("");
                    setCopyDialogOpen(true);
                  }}
                >
                  کپی از شماره
                </Button>
                <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openCreateDialog}>
                  پیش‌فاکتور جدید
                </Button>
              </Stack>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="جستجو نام / موبایل / شماره پیش‌فاکتور"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(searchText)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <Button variant="contained" onClick={() => setAppliedSearch(searchText)}>
                اعمال فیلتر
              </Button>
            </Box>
            <EnhancedTable
              rows={tableRows}
              headCells={[
                { id: "row_index", label: "ردیف" },
                { id: "proformaNumber", label: "شماره" },
                { id: "issuedAtDisplay", label: "تاریخ" },
                { id: "buyerName", label: "خریدار" },
                { id: "buyerMobile", label: "موبایل" },
                { id: "totalDisplay", label: "مبلغ قابل پرداخت" },
                { id: "statusTitle", label: "وضعیت" },
              ]}
              actions={[
                {
                  icon: <EditOutlinedIcon fontSize="small" />,
                  tooltip: "ویرایش",
                  hidden: (row) => !row.isEditable,
                  onClick: (row) => openEdit(row.id),
                },
                {
                  icon: <ContentCopyIcon fontSize="small" />,
                  tooltip: "کپی با شماره جدید",
                  onClick: (row) => {
                    setCopySourceNumber(String(row.proformaNumber || ""));
                    setCopyDialogOpen(true);
                  },
                },
                {
                  icon: <LockOutlinedIcon fontSize="small" />,
                  tooltip: "نهایی‌سازی و قفل",
                  hidden: (row) => !row.isEditable,
                  onClick: async (row) => {
                    if (
                      !window.confirm(
                        `پیش‌فاکتور ${row.proformaNumber} نهایی و قفل شود؟ بعد از این قابل ویرایش نیست.`
                      )
                    ) {
                      return;
                    }
                    try {
                      const res = await SalesProformaApi.finalize(source, row.id);
                      if (res?.isSuccess === false) {
                        toast.error(res?.message || "نهایی‌سازی ناموفق بود");
                        return;
                      }
                      toast.success(res?.message || "نهایی و قفل شد");
                      await loadList();
                    } catch (e) {
                      toast.error(e?.message || "نهایی‌سازی ناموفق بود");
                    }
                  },
                },
                {
                  icon: <DeleteOutlineIcon fontSize="small" color="error" />,
                  tooltip: "حذف",
                  hidden: (row) => !row.isEditable,
                  onClick: (row) => deleteDraft(row.id),
                },
                {
                  icon: <PictureAsPdfIcon fontSize="small" />,
                  tooltip: "دانلود PDF",
                  hidden: (row) => !row.canDownload,
                  onClick: (row) => downloadPdf(row.id, row.proformaNumber),
                },
                {
                  icon: <DownloadIcon fontSize="small" />,
                  tooltip: "دانلود اکسل",
                  hidden: (row) => !row.canDownload,
                  onClick: (row) => downloadExcel(row.id, row.proformaNumber),
                },
              ]}
              loading={loading || loadingDraft}
              showCheckbox={false}
              dense
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>
              {isEditingExisting
                ? formStatus === STATUS_ISSUED
                  ? "ویرایش پیش‌فاکتور صادرشده"
                  : "ادامه پیش‌نویس پیش‌فاکتور"
                : "پیش‌فاکتور جدید"}
            </span>
            {form.proformaNumber ? (
              <Chip size="small" color="primary" label={`شماره ${form.proformaNumber}`} />
            ) : null}
            {formStatus === STATUS_DRAFT ? <Chip size="small" color="warning" label="پیش‌نویس" /> : null}
            {formStatus === STATUS_ISSUED ? <Chip size="small" color="info" label="صادر شده" /> : null}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              ذخیره تغییرات در هر مرحله ممکن است. برای قفل دائمی از «نهایی‌سازی و قفل» استفاده کنید. کپی از شماره مبدأ هم از دکمه بالای صفحه در دسترس است.
            </Alert>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                label="کد پیگیری درخواست (اختیاری)"
                size="small"
                value={form.requestTrackingCode}
                onChange={(e) => setForm((f) => ({ ...f, requestTrackingCode: e.target.value }))}
                sx={{ minWidth: 240 }}
              />
              <Button variant="outlined" onClick={loadFromRequest} disabled={loadingRequest}>
                {loadingRequest ? "در حال بارگذاری…" : "بارگذاری از پرونده"}
              </Button>
            </Stack>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                خریدار
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={customerMode}
                onChange={(_, value) => {
                  if (!value) return;
                  setCustomerMode(value);
                  if (value === "new") {
                    setSelectedCustomer(null);
                    setForm((f) => ({ ...f, salesCustomerId: null }));
                  } else {
                    setCustomerQuery("");
                    loadCustomers("");
                  }
                }}
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="existing">مشتری قبلی</ToggleButton>
                <ToggleButton value="new">مشتری جدید</ToggleButton>
              </ToggleButtonGroup>

              {customerMode === "existing" ? (
                <Autocomplete
                  options={customerOptions}
                  loading={customerLoading}
                  value={selectedCustomer}
                  inputValue={customerQuery}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    if (typeof option === "string") return option;
                    const name = pick(option, "fullName") || "";
                    const nid = pick(option, "nationalId") || "";
                    const mobile = pick(option, "mobile") || "";
                    return [name, nid, mobile].filter(Boolean).join(" — ");
                  }}
                  isOptionEqualToValue={(a, b) => Number(pick(a, "id")) === Number(pick(b, "id"))}
                  onInputChange={(_, value) => setCustomerQuery(value)}
                  onChange={(_, value) => {
                    if (value && typeof value === "object") applyCustomer(value);
                    else applyCustomer(null);
                  }}
                  noOptionsText={
                    customerLoading
                      ? "در حال جستجو…"
                      : "مشتری یافت نشد — حالت «مشتری جدید» را انتخاب کنید"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="انتخاب از مشتریان قبلی (نام / کد ملی / موبایل)"
                      helperText="با باز شدن فرم آخرین مشتریان نمایش داده می‌شود؛ برای فیلتر تایپ کنید."
                    />
                  )}
                />
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  مشخصات مشتری جدید را در فیلدهای زیر وارد کنید؛ با صدور یا ذخیره پیشنویس در فهرست مشتریان بازرگانی ثبت می‌شود.
                </Typography>
              )}
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="نام خریدار"
                  size="small"
                  fullWidth
                  required
                  value={form.buyerName}
                  onChange={(e) => setForm((f) => ({ ...f, buyerName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="موبایل"
                  size="small"
                  fullWidth
                  value={form.buyerMobile}
                  onChange={(e) => setForm((f) => ({ ...f, buyerMobile: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="شناسه ملی"
                  size="small"
                  fullWidth
                  value={form.buyerNationalId}
                  onChange={(e) => setForm((f) => ({ ...f, buyerNationalId: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="کد پستی"
                  size="small"
                  fullWidth
                  value={form.buyerPostalCode}
                  onChange={(e) => setForm((f) => ({ ...f, buyerPostalCode: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="کد اقتصادی"
                  size="small"
                  fullWidth
                  value={form.buyerEconomicCode}
                  onChange={(e) => setForm((f) => ({ ...f, buyerEconomicCode: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="اعتبار قیمت (ساعت)"
                  size="small"
                  type="number"
                  fullWidth
                  value={form.validityHours}
                  onChange={(e) => setForm((f) => ({ ...f, validityHours: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="نشانی"
                  size="small"
                  fullWidth
                  value={form.buyerAddress}
                  onChange={(e) => setForm((f) => ({ ...f, buyerAddress: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2">اقلام</Typography>
            {computedLines.map((line, index) => (
              <Grid container spacing={1} key={index} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    label="شرح کالا"
                    size="small"
                    fullWidth
                    value={form.lines[index].title}
                    onChange={(e) => setLine(index, "title", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <TextField
                    label="واحد"
                    size="small"
                    fullWidth
                    value={form.lines[index].unit}
                    onChange={(e) => setLine(index, "unit", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <TextField
                    label="تعداد"
                    size="small"
                    type="number"
                    fullWidth
                    value={form.lines[index].quantity}
                    onChange={(e) => setLine(index, "quantity", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="قیمت واحد"
                    size="small"
                    type="number"
                    fullWidth
                    value={form.lines[index].unitPriceRial}
                    onChange={(e) => setLine(index, "unitPriceRial", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <TextField
                    label="تخفیف"
                    size="small"
                    type="number"
                    fullWidth
                    value={form.lines[index].discountRial}
                    onChange={(e) => setLine(index, "discountRial", e.target.value)}
                  />
                </Grid>
                <Grid item xs={10} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    مالیات ۱۰٪: {formatRial(line.tax)}
                    <br />
                    جمع: {formatRial(line.total)}
                  </Typography>
                </Grid>
                <Grid item xs={2} md={1}>
                  <IconButton
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        lines: f.lines.length === 1 ? [emptyLine()] : f.lines.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))}>
              افزودن قلم
            </Button>
            <Typography variant="body2">
              جمع پس از تخفیف: {formatRial(totals.after)} — مالیات: {formatRial(totals.tax)} — قابل پرداخت:{" "}
              <b>{formatRial(totals.total)}</b>
            </Typography>
            <TextField
              label="توضیحات و شرایط"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            بستن
          </Button>
          {isEditingExisting && canEditCurrent ? (
            <Button color="error" disabled={saving} onClick={() => deleteDraft(form.id)}>
              حذف
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            onClick={saveDraft}
            disabled={saving || !canEditCurrent}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
          >
            ذخیره تغییرات
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={issue}
            disabled={saving || !canEditCurrent}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            صدور و دانلود PDF
          </Button>
          {canFinalizeCurrent ? (
            <Button
              variant="contained"
              color="warning"
              onClick={finalize}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <LockOutlinedIcon />}
            >
              نهایی‌سازی و قفل
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog
        open={copyDialogOpen}
        onClose={() => !saving && setCopyDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>کپی پیش‌فاکتور</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              شماره پیش‌فاکتور مبدأ را وارد کنید. یک نسخه جدید با شماره تازه ساخته می‌شود و قابل ویرایش خواهد بود.
            </Typography>
            <TextField
              autoFocus
              label="شماره پیش‌فاکتور مبدأ"
              size="small"
              fullWidth
              value={copySourceNumber}
              onChange={(e) => setCopySourceNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && copyFromNumber()}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={copyFromNumber}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon />}
          >
            کپی و باز کردن برای ویرایش
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SalesProformaPage;
